// SKILLSMITH-OPPORTUNITIES (founder, 2026-10-30): helpers become a revenue stream. Skillsmith
// notices that a DE keeps failing at something, works outside its helpers, or that the client keeps
// correcting it, and suggests to US (the platform's super admins, never the client) that a new
// helper or skill would fix it. A super admin then drafts and sends the client an offer email, or
// a paid helper quote (helper-offers.ts), and walks the opportunity along a simple pipeline:
// Suggested → Offered → Accepted / Declined → Added, with the revenue entered by hand.
//
// Isolation (already decided for Skillsmith): it never contacts a client or an agent; nothing
// reaches a client until a super admin presses Send; it reads only the issue log and aggregate
// counts (`opportunitySignals`: run failures, rework, corrections, issue entries per DE — never
// chats or message text). Every route below answers 404 to anyone but a platform super admin
// holding a live operator grant on the company, except the two the company's system agent calls
// with its own key (`signals:read` reads the counts, `feedback:write` files an opportunity).
import { z } from "zod";
import {
  Currency,
  OpportunityConfidence,
  OpportunitySource,
  OpportunityStage,
  OpportunitySuggestionKind,
} from "../enums";
import { Slug, Timestamp, Uuid } from "../primitives";
import { HelperOffer } from "./helper-offers";
import { defineRoute } from "./route";

const base = "/v1/operator";
const core = { feature: "core", tag: "operator", access: "operator" } as const;

const HubCompany = z.object({ id: Uuid, slug: Slug, name: z.string() });
const SystemAgentParams = z.object({ company_id: Uuid, membership_id: Uuid });
const OpportunityParams = z.object({ company_id: Uuid, opportunity_id: Uuid });

/** Counts behind an opportunity: `{run_failures: 7, corrections: 3, …}` (at most 20 keys). */
export const OpportunityCounts = z
  .record(
    z
      .string()
      .max(40)
      .regex(/^[a-z][a-z0-9_]{0,39}$/),
    z.int().min(0).max(1_000_000),
  )
  .refine((o) => Object.keys(o).length <= 20, "at most 20 counts")
  .meta({ maxProperties: 20 });

export const OpportunityEvidence = z.object({
  kind: z.string().min(1).max(64),
  id: z.string().min(1).max(200),
  note: z.string().max(500).optional(),
});

/** The offer email as drafted and edited (recipients: the company's people). */
export const OpportunityOffer = z.object({
  subject: z.string().nullable(),
  body: z.string().nullable(),
  to: z.array(Uuid),
  edited_at: Timestamp.nullable(),
  sent_at: Timestamp.nullable(),
});

export const AgentOpportunity = z
  .object({
    id: Uuid,
    company: HubCompany,
    source: OpportunitySource,
    /** The DE it is about (DE label in the UI); null when a request named none. */
    de: z.object({ membership_id: Uuid, name: z.string() }).nullable(),
    title: z.string(),
    problem: z.string(),
    evidence_counts: z.record(z.string(), z.int()),
    evidence: z.array(OpportunityEvidence),
    suggestion_kind: OpportunitySuggestionKind,
    suggested_name: z.string(),
    suggested_what: z.string(),
    expected_benefit: z.string(),
    confidence: OpportunityConfidence,
    stage: OpportunityStage,
    request_ref: z.string().nullable(),
    offer: OpportunityOffer,
    /** The paid helper quote sent for it, if any. */
    helper_offer: HelperOffer.nullable(),
    amount_minor: z.int().nullable(),
    currency: Currency,
    stage_changed_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "AgentOpportunity" });

const StageCounts = z.object({
  suggested: z.int().min(0),
  offered: z.int().min(0),
  accepted: z.int().min(0),
  declined: z.int().min(0),
  added: z.int().min(0),
  dismissed: z.int().min(0),
});

export const AgentOpportunitiesQuery = z.object({
  company_id: Uuid.optional(),
  stage: OpportunityStage.optional(),
});

/** The pipeline: counts per stage and revenue (accepted + added), plus the opportunities. */
export const AgentOpportunities = z
  .object({
    pipeline: z.object({
      counts: StageCounts,
      /** Revenue per currency (accepted and added; the entered amount, else the quote's). */
      revenue: z.array(z.object({ currency: Currency, amount_minor: z.int().min(0) })),
    }),
    /** Newest first (at most 200 per company); filtered by `company_id` / `stage`. */
    data: z.array(AgentOpportunity),
    /** Companies the hub spans (the filter's choices). */
    companies: z.array(HubCompany),
  })
  .meta({ id: "AgentOpportunities" });

/** One opportunity with the company's people (the offer's possible recipients). */
export const AgentOpportunityDetail = z
  .object({
    opportunity: AgentOpportunity,
    people: z.array(
      z.object({
        membership_id: Uuid,
        name: z.string(),
        email: z.string().nullable(),
        role: z.enum(["owner", "admin", "member"]),
      }),
    ),
  })
  .meta({ id: "AgentOpportunityDetail" });

export const AgentOpportunityDraftPut = z
  .strictObject({
    subject: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(5000),
    to: z.array(Uuid).min(1).max(20),
  })
  .meta({ id: "AgentOpportunityDraftPut" });

/** A move along the pipeline. `accepted` / `added` may carry the amount it sold for. */
export const AgentOpportunityStagePut = z
  .strictObject({
    stage: z.enum(["suggested", "dismissed", "accepted", "declined", "added"]),
    amount_minor: z.int().min(0).max(100_000_000_00).optional(),
    currency: Currency.optional(),
  })
  .meta({ id: "AgentOpportunityStagePut" });

/** What the system agent reads to spot opportunities: counts per DE only. */
export const OpportunitySignalsQuery = z.object({
  days: z.coerce.number().int().min(1).max(90).optional().describe("Default 30"),
});

export const OpportunitySignals = z
  .object({
    since: Timestamp,
    des: z.array(
      z.object({
        membership_id: Uuid,
        name: z.string(),
        runs: z.int().min(0),
        failed_runs: z.int().min(0),
        /** Reviewer verdicts `not_verified` (work sent back). */
        rework: z.int().min(0),
        /** Retried runs (a successor of a lost run). */
        retries: z.int().min(0),
        /** People's corrections of its briefs. */
        corrections: z.int().min(0),
        /** Runs that used no skill pack (work done outside its helpers and skills). */
        runs_without_skills: z.int().min(0),
        /** Issue-log entries about it, per kind. */
        issues: z.record(z.string(), z.int().min(0)),
        /** Up to 5 recent issue-log entries about it (the log's own short text, cut). */
        examples: z.array(z.object({ issue_id: Uuid, kind: z.string(), what: z.string() })),
        /** Its helpers (AG names) — so an existing one is not suggested again. */
        helpers: z.array(z.string()),
      }),
    ),
    /** Open opportunities already filed (never suggest one twice). */
    open: z.array(
      z.object({
        de_membership_id: Uuid.nullable(),
        suggested_name: z.string(),
        stage: OpportunityStage,
      }),
    ),
  })
  .meta({ id: "OpportunitySignals" });

export const SystemAgentOpportunityCreate = z
  .strictObject({
    /** The company it is about: must be the path's (never re-homed). */
    company_id: Uuid,
    de_membership_id: Uuid,
    title: z.string().trim().min(1).max(200),
    problem: z.string().trim().min(1).max(2000),
    evidence_counts: OpportunityCounts,
    evidence: z.array(OpportunityEvidence).max(20).optional(),
    suggestion_kind: OpportunitySuggestionKind,
    suggested_name: z.string().trim().min(1).max(120),
    suggested_what: z.string().trim().min(1).max(2000),
    expected_benefit: z.string().trim().min(1).max(1000),
    confidence: OpportunityConfidence,
    /** Skillsmith's draft of the client email (the super admin edits it before any Send). */
    offer_draft: z
      .strictObject({
        subject: z.string().trim().min(1).max(200),
        body: z.string().trim().min(1).max(5000),
      })
      .optional(),
  })
  .meta({ id: "SystemAgentOpportunityCreate" });

/** Filed (201), or the open one for the same DE and suggestion (200, unchanged). */
export const SystemAgentOpportunityFiled = z
  .object({ id: Uuid, created: z.boolean(), stage: OpportunityStage })
  .meta({ id: "SystemAgentOpportunityFiled" });

export const opportunityRoutes = {
  "operator.systemAgents.opportunitySignals": defineRoute({
    ...core,
    id: "operator.systemAgents.opportunitySignals",
    method: "get",
    path: `${base}/system-agents/{company_id}/{membership_id}/opportunity-signals`,
    summary:
      "What a system agent reads to spot helper opportunities: per-DE counts (failures, rework, corrections, issue entries, runs without skills), its helpers' names, the open opportunities (read only)",
    params: SystemAgentParams,
    query: OpportunitySignalsQuery,
    response: { status: 200, schema: OpportunitySignals },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.systemAgents.opportunities.create": defineRoute({
    ...core,
    id: "operator.systemAgents.opportunities.create",
    method: "post",
    path: `${base}/system-agents/{company_id}/{membership_id}/opportunities`,
    summary:
      "File a helper or skill opportunity in the company's Agent health topic (the operator-only store; once per DE and suggestion)",
    params: SystemAgentParams,
    body: SystemAgentOpportunityCreate,
    // Operator-only store: no company state, and an event would show the company its system agent.
    safe: true,
    response: { status: 201, schema: SystemAgentOpportunityFiled },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
  "operator.opportunities.list": defineRoute({
    ...core,
    id: "operator.opportunities.list",
    method: "get",
    path: `${base}/opportunities`,
    summary:
      "The helper sales pipeline across every granted company: counts per stage, revenue, and the opportunities (super admins only)",
    query: AgentOpportunitiesQuery,
    response: { status: 200, schema: AgentOpportunities },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.opportunities.get": defineRoute({
    ...core,
    id: "operator.opportunities.get",
    method: "get",
    path: `${base}/opportunities/{company_id}/{opportunity_id}`,
    summary: "One opportunity with the company's people (the offer's possible recipients)",
    params: OpportunityParams,
    response: { status: 200, schema: AgentOpportunityDetail },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.opportunities.draft": defineRoute({
    ...core,
    id: "operator.opportunities.draft",
    method: "put",
    path: `${base}/opportunities/{company_id}/{opportunity_id}/draft`,
    summary: "Save the offer email as the super admin edited it (subject, body, recipients)",
    params: OpportunityParams,
    body: AgentOpportunityDraftPut,
    safe: true,
    response: { status: 200, schema: AgentOpportunity },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
  "operator.opportunities.send": defineRoute({
    ...core,
    id: "operator.opportunities.send",
    method: "post",
    path: `${base}/opportunities/{company_id}/{opportunity_id}/send`,
    summary:
      "Send the offer email through the platform's mailer (a super admin's Send; audited); the opportunity moves to Offered",
    params: OpportunityParams,
    idempotency: "optional",
    // No company state: the opportunity is operator-only and the send is audited platform-side.
    safe: true,
    response: { status: 200, schema: AgentOpportunity },
    // `vendor_unavailable`: the mailer refused or failed (nothing recorded as sent).
    errors: ["not_found", "conflict", "vendor_unavailable"],
    emits: [],
  }),
  "operator.opportunities.stage": defineRoute({
    ...core,
    id: "operator.opportunities.stage",
    method: "post",
    path: `${base}/opportunities/{company_id}/{opportunity_id}/stage`,
    summary:
      "Move an opportunity: dismiss, reopen, accepted / declined, added (sold; the amount by hand); audited",
    params: OpportunityParams,
    body: AgentOpportunityStagePut,
    safe: true,
    response: { status: 200, schema: AgentOpportunity },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
} as const;
